import handlebars from 'handlebars';
import { Pair } from 'src/adl-gen/sys/types';

import { makeWithDbId, WithDbId } from '../adl-gen/common/db';
import * as DB from '../adl-gen/totalinteraction/futurefit/db';
// import { TrainingOffering } from "../adl-gen/totalinteraction/futurefit/db";

/// wrap a synchronous function to return a promise
export function asPromiseFunction<T>(syncFunc: () => T): () => Promise<T> {
  return () => {
    return new Promise<T>((resolve, reject) => {
      try {
        resolve(syncFunc());
      } catch (err) {
        reject(err);
      }
    });
  };
}

/// Obtain the array of values from a StringMap object
export function objectIdsValues<T>(stringMap: { [key: string]: T }): WithDbId<T>[] {
  return Object.keys(stringMap).map(key => makeWithDbId({ id: key, value: stringMap[key] }));
}

/// return a comparison function for sort - based on sort by result from provided accessor function
export function compareByGet<T, K>(get: (t: T) => K): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const valA: K = get(a);
    const valB: K = get(b);
    return valA < valB ? -1 : valA > valB ? 1 : 0;
  };
}

/// Comparison function for sort on two separate properties, eg. a numeric one and a string.
/// the get function should return a pair of values.
export function compareByGetTwoProperties<T, K1, K2>(get: (t: T) => Pair<K1, K2>): (a: T, b: T) => number {
  return (a: T, b: T) => {
    const valA: Pair<K1, K2> = get(a);
    const valB: Pair<K1, K2> = get(b);
    return valA.v1 < valB.v1 ? -1 : valA.v1 > valB.v1 ? 1 : valA.v2 < valB.v2 ? -1 : valA.v2 > valB.v2 ? 1 : 0;
  };
}

export function average(array: (number | undefined)[]) {
  if (array.length === 0) {
    return 0;
  }
  const numbers: number[] = array.filter(a => a !== undefined) as number[];
  return numbers.reduce((p, c) => p + c, 0) / numbers.length;
}

/// Confirm a string is really a number
export function isNumeric(str: string): boolean {
  // tslint:disable-next-line: strict-type-predicates
  if (typeof str !== 'string') {
    return false;
  }
  return !isNaN(parseFloat(str));
}

/// Confirm a string is an integer
export function isInt(str: string): boolean {
  return isNumeric(str) && Number.isInteger(parseFloat(str));
}

/// Random number generator up to 6 digits
export function uniqueId(): number {
  return Math.floor(Math.random() * Date.now());
}

/// For use with uniqueId and not implying a persistent (DB) ID
export interface WithId<T> {
  id: number;
  value: T;
}

/// Compare two arrays for equality, item by item - order important
export function orderedArraysEqual<T>(a: Array<T>, b: Array<T>): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export type ViewType = { ['TrainingOffering']: Omit<DB.TrainingOffering, 'description'> };

/**
 * Inject Handlebars variables {{...}} into Mardkwon text before passing to renderer.
 * For example for a TrainingOffering, fields can be referenced like this:
 * {{TrainingOffering.executeUrl}}
 * NB Careful - obvs it would be stupid to inject TrainingOffering.description (a MD template) into itself.
 */
export function renderTemplate(template: string, view: ViewType) {
  const outputTemplate = handlebars.compile(template);
  /// https://stackoverflow.com/questions/8044219/how-do-i-access-an-access-array-item-by-index-in-handlebars
  /// Reference individual array items in the template as (eg)
  ///  { people: [ { name: "Name 1" }, { name: "name 2" }, ... ] } and {{people.1.name}} (zero-based) -> name 2
  return outputTemplate(view);
}

/// ===== [ Use Base64 data URI; send base64 to server; decode on server ] =====
// Utility function. Read a file as a data-uri string. This allows the browser to
// handle the Base64 encoding. We can parse out the data and mime-type from the resulting
// string. Then send to server using API and let the server decode the string and arrange
// for it to be stored as a file
export async function readFileAsUri(file: File): Promise<string> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onabort = () => {
      // console.log("file reading was aborted");
      reject(new DOMException('file->dataUri reading was aborted'));
    };
    reader.onerror = () => {
      reader.abort();
      // console.log("file reading has failed");
      reject(reader.error);
    };
    ///
    /// --- [ Possible TODO Move some/all of this to reader.onloadend ] ---
    ///
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.readAsDataURL(file);
  });
}

export async function readFileAsText(file: File): Promise<string> {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.onabort = () => {
      // console.log("file reading was aborted");
      reject(new DOMException('file->text reading was aborted'));
    };
    reader.onerror = () => {
      reader.abort();
      // console.log("file reading has failed");
      reject(reader.error);
    };
    ///
    /// --- [ Possible TODO Move some/all of this to reader.onload ] ---
    ///
    reader.onloadend = () => {
      resolve(reader.result as string);
    };
    reader.readAsText(file);
  });
}

/// FileReader.readAsDataURL returns the entire file Base64-encoded with the following prefix:
/// data:*/*;base64, (RE /^data:.+/.+;base64,/)
/// where '*/*' represents the content-type eg image/png or text/plain
export function extractContentTypeFromDataUri(dataUri: string): string {
  let contentType = '';
  const contentTypeStrings = dataUri.match(/^data:([^;]+);/);
  if (contentTypeStrings !== null) {
    contentType = contentTypeStrings[1];
  }
  return contentType;
}

export function removePreambleFromDataUri(dataUri: string): string {
  return dataUri.replace(/^[^,]+,/, '');
}

// Convert caps and leading non-alphanumeric chars to '_'
// If there's a non-alpha just before a capital, just emit one '_'
// See https://www.w3resource.com/javascript-exercises/fundamental/javascript-fundamental-exercise-120.php
export const toSnake = (str: string) => {
  const match = str.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g);
  return match ? match.map(x => x.toLowerCase()).join('_') : str;
};



// WEBPACK FOOTER //
// ./src/util/utils.ts